import React, { useEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  COLORS,
  RADIUS,
  SPACING,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useTelemetry } from '@order/Telemetry';

import type { useCheckoutTelemetry } from '../../hooks';

export const CheckoutLayout = (props: CheckoutLayoutProps) => {
  const { matchByBreakpointIndex } = useBreakpointHelpers();
  const style = matchByBreakpointIndex([{}, {}, styles.layoutDesktopUp]);

  // ───── Telemetry ────────────────────────────────────────────────
  const { checkoutViewEvent, children } = props;
  const { trackEvent } = useTelemetry();

  useEffect(() => {
    if (!checkoutViewEvent?.current) return;
    trackEvent(checkoutViewEvent.current);
  }, [checkoutViewEvent, trackEvent]);

  return <View style={style}>{children}</View>;
};

//
// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────
//

export const CheckoutLayoutColumn = ({ children }: ViewProps) => {
  const { matchByBreakpointIndex } = useBreakpointHelpers();
  const style = matchByBreakpointIndex([{}, {}, styles.columnDesktopUp]);

  return <View style={style}>{children}</View>;
};

export const CheckoutLayoutBlock = ({ testID, children }: ViewProps) => {
  const { currentBreakpoint, matchByBreakpointIndex } = useBreakpointHelpers();
  const layoutBlockResponsiveStyle = currentBreakpoint.isMobile
    ? styles.layoutBlockMobile
    : styles.layoutBlockTabletUp;
  const layoutBlockInnerResponsiveStyle = matchByBreakpointIndex([
    {},
    styles.layoutBlockInnerTablet,
    styles.layoutBlockInnerDesktopUp,
  ]);
  const layoutBlockInnerStyle = [
    styles.layoutBlockInner,
    layoutBlockInnerResponsiveStyle,
  ];

  return (
    <View testID={testID} style={layoutBlockResponsiveStyle}>
      <View style={layoutBlockInnerStyle}>{children}</View>
    </View>
  );
};

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const GUTTER_SIZE = SPACING.large / 2;

const styles = StyleSheet.create({
  layoutDesktopUp: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginRight: -GUTTER_SIZE,
    marginLeft: -GUTTER_SIZE,
  },

  columnDesktopUp: {
    flexBasis: '50%',
    paddingHorizontal: GUTTER_SIZE,
  },

  layoutBlockMobile: {
    paddingBottom: SPACING.xlarge,
  },
  layoutBlockTabletUp: {
    paddingBottom: SPACING.large,
  },
  layoutBlockInner: {
    backgroundColor: COLORS.OATMEAL,
  },
  layoutBlockInnerTablet: {
    padding: SPACING.medium * 2,
    borderRadius: RADIUS.large,
  },
  layoutBlockInnerDesktopUp: {
    padding: SPACING.xlarge,
    borderRadius: RADIUS.medium,
  },
});

type CheckoutLayoutProps = ViewProps & {
  readonly checkoutViewEvent?: ReturnType<
    typeof useCheckoutTelemetry
  >['checkoutViewEvent'];
};
